import { taskReferenceParts } from "@/lib/task-reference";
import { useMemo, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { useNavigate } from "react-router";

import {
  type CarrierDispatchLookupCustomer,
  type CarrierDispatchLookupDriver,
  type CarrierDispatchLookupShipment,
  type CarrierDispatchLookupTask,
  type CarrierDispatchLookupTrip,
  type CarrierTrip,
  useCarrierDispatchLookup,
} from "@/api/carrier";
import { StatusBadge } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { labelize } from "./dispatch-presenters";

type LookupResult =
  | { kind: "trip"; row: CarrierDispatchLookupTrip }
  | { kind: "task"; row: CarrierDispatchLookupTask }
  | { kind: "shipment"; row: CarrierDispatchLookupShipment }
  | { kind: "driver"; row: CarrierDispatchLookupDriver }
  | { kind: "customer"; row: CarrierDispatchLookupCustomer };

const EMPTY_RESULTS = {
  trips: [],
  tasks: [],
  shipments: [],
  drivers: [],
  customers: [],
};

export function LookupPanel({
  trips,
  onClose,
  onOpenTrip,
  showHeader = true,
  replaceNavigation = false,
}: {
  trips: CarrierTrip[];
  onClose: () => void;
  onOpenTrip: (trip: CarrierTrip) => void;
  showHeader?: boolean;
  replaceNavigation?: boolean;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const search = query.trim();
  const lookup = useCarrierDispatchLookup(search, {
    enabled: Boolean(search),
    limit: 8,
  });
  const data = lookup.data ?? EMPTY_RESULTS;
  const results = useMemo<LookupResult[]>(
    () => [
      ...data.trips.map((row) => ({ kind: "trip" as const, row })),
      ...data.tasks.map((row) => ({ kind: "task" as const, row })),
      ...data.shipments.map((row) => ({ kind: "shipment" as const, row })),
      ...data.drivers.map((row) => ({ kind: "driver" as const, row })),
      ...data.customers.map((row) => ({ kind: "customer" as const, row })),
    ],
    [data]
  );
  const navigateFromLookup = (destination: string) => {
    onClose();
    navigate(destination, { replace: replaceNavigation });
  };

  const selectResult = (result: LookupResult) => {
    if (result.kind === "trip") {
      const loadedTrip = trips.find((trip) => trip.id === result.row.id);
      if (loadedTrip) {
        onOpenTrip(loadedTrip);
        return;
      }
      navigateFromLookup(
        `/trips?tripId=${encodeURIComponent(result.row.id)}&q=TRP-${result.row.tripNumber}`
      );
      return;
    }

    const destination =
      result.kind === "task"
        ? `/trips/tasks?taskId=${encodeURIComponent(result.row.id)}&q=TSK-${result.row.taskNumber}`
        : result.kind === "shipment"
          ? `/fulfillment/shipments?shipmentId=${encodeURIComponent(result.row.id)}`
          : result.kind === "driver"
            ? `/team?operatorId=${encodeURIComponent(result.row.id)}&q=${encodeURIComponent(result.row.name)}`
            : `/fulfillment/orders?orderId=${encodeURIComponent(result.row.orderId)}&q=${result.row.orderNumber}`;
    navigateFromLookup(destination);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        current <= 0 ? results.length - 1 : current - 1
      );
    } else if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(results.length - 1);
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      selectResult(results[activeIndex]);
    }
  };

  let resultIndex = 0;
  return (
    <div className="flex h-full flex-col">
      {showHeader && (
        <div className="flex h-14 items-center justify-between border-b px-3">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Lookup</h2>
            <p className="text-xs text-zinc-500">
              Visible Carrier records only
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="h-11 w-11 lg:h-8 lg:w-8"
            aria-label="Close lookup"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="border-b p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input
            autoFocus
            role="combobox"
            aria-label="Lookup dispatch records"
            aria-autocomplete="list"
            aria-controls="dispatch-lookup-results"
            aria-expanded={Boolean(search)}
            aria-activedescendant={
              activeIndex >= 0
                ? `dispatch-lookup-result-${activeIndex}`
                : undefined
            }
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(-1);
            }}
            onKeyDown={onInputKeyDown}
            placeholder="Trip, task, shipment, driver, customer"
            className="h-11 pl-8 text-sm lg:h-9"
          />
        </div>
      </div>

      <div
        id="dispatch-lookup-results"
        role="listbox"
        aria-label="Dispatch lookup results"
        className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3"
      >
        {!search && (
          <p className="text-sm text-zinc-500">
            Search visible dispatch records by number, name, contact, or
            vehicle.
          </p>
        )}
        {search && lookup.isLoading && (
          <div
            role="status"
            aria-label="Searching dispatch records"
            className="flex items-center gap-2 text-sm text-zinc-500"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Searching...
          </div>
        )}
        {search && lookup.error && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
            <span>Lookup may be incomplete.</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 bg-white px-3 text-xs lg:h-7 lg:px-2"
              onClick={() => void lookup.refetch()}
            >
              Retry lookup
            </Button>
          </div>
        )}
        {search &&
          !lookup.isLoading &&
          results.length === 0 &&
          !lookup.error && (
            <p className="text-sm text-zinc-500">
              No visible dispatch records match “{search}”.
            </p>
          )}

        <LookupGroup title="Trips" rows={data.trips}>
          {(row) => {
            const index = resultIndex++;
            return (
              <LookupOption
                key={row.id}
                index={index}
                active={activeIndex === index}
                primary={`TRP-${row.tripNumber}`}
                status={row.status}
                secondary={row.driverName ?? "Unassigned"}
                detail={row.dispatchCenterName ?? "No Dispatch Center"}
                onSelect={() => selectResult({ kind: "trip", row })}
              />
            );
          }}
        </LookupGroup>
        <LookupGroup title="Tasks" rows={data.tasks}>
          {(row) => {
            const index = resultIndex++;
            const references = taskReferenceParts(row);
            return (
              <LookupOption
                key={row.id}
                index={index}
                active={activeIndex === index}
                primary={references.primary}
                status={row.status}
                secondary={`${references.secondary} · ${labelize(row.type)}`}
                detail={row.location ?? row.dispatchCenterName ?? "No location"}
                onSelect={() => selectResult({ kind: "task", row })}
              />
            );
          }}
        </LookupGroup>
        <LookupGroup title="Shipments" rows={data.shipments}>
          {(row) => {
            const index = resultIndex++;
            return (
              <LookupOption
                key={row.id}
                index={index}
                active={activeIndex === index}
                primary={`SHP-${row.shipmentNumber}`}
                status={row.status}
                secondary={row.trackingNumber ?? "No tracking number"}
                detail={
                  row.orderNumber
                    ? `ORD-${row.orderNumber}`
                    : "Order unavailable"
                }
                onSelect={() => selectResult({ kind: "shipment", row })}
              />
            );
          }}
        </LookupGroup>
        <LookupGroup title="Drivers" rows={data.drivers}>
          {(row) => {
            const index = resultIndex++;
            return (
              <LookupOption
                key={row.id}
                index={index}
                active={activeIndex === index}
                primary={row.name || "Unnamed driver"}
                status={row.status}
                secondary={row.vehicleCode ?? "No vehicle"}
                detail={row.dispatchCenterName ?? "No Dispatch Center"}
                onSelect={() => selectResult({ kind: "driver", row })}
              />
            );
          }}
        </LookupGroup>
        <LookupGroup title="Customers" rows={data.customers}>
          {(row) => {
            const index = resultIndex++;
            return (
              <LookupOption
                key={row.id}
                index={index}
                active={activeIndex === index}
                primary={row.name || `CUS-${row.customerNumber}`}
                secondary={`CUS-${row.customerNumber}`}
                detail={
                  row.companyName ??
                  row.email ??
                  row.phone ??
                  "No contact details"
                }
                onSelect={() => selectResult({ kind: "customer", row })}
              />
            );
          }}
        </LookupGroup>
      </div>

      {/*
        The shortcut list lives here rather than behind its own header button.
        Someone hunting for a keystroke is already looking for a way to move
        faster, and this is the panel that opens with one.
      */}
      <dl className="shrink-0 space-y-1.5 border-t bg-zinc-50/70 px-3 py-2.5 text-[11px]">
        <ShortcutRow label="Open lookup" keys={["⌘ / Ctrl K", "F on map"]} />
        <ShortcutRow label="Box selection" keys={["S on map"]} />
        <ShortcutRow label="Dismiss current action" keys={["Esc"]} />
        <p className="pt-0.5 text-[10.5px] text-zinc-500">
          Plain-letter shortcuts pause while you are typing.
        </p>
      </dl>
    </div>
  );
}

function ShortcutRow({ label, keys }: { label: string; keys: string[] }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-zinc-600">{label}</dt>
      <dd className="flex gap-1">
        {keys.map((key) => (
          <kbd
            key={key}
            className="rounded border bg-white px-1 font-mono text-[10px] text-zinc-500"
          >
            {key}
          </kbd>
        ))}
      </dd>
    </div>
  );
}

function LookupGroup<T>({
  title,
  rows,
  children,
}: {
  title: string;
  rows: T[];
  children: (row: T) => React.ReactNode;
}) {
  if (rows.length === 0) return null;
  return (
    <fieldset aria-label={title} className="space-y-1.5">
      <div className="flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
        <span>{title}</span>
        <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
          {rows.length}
        </Badge>
      </div>
      <div className="space-y-1">{rows.map(children)}</div>
    </fieldset>
  );
}

function LookupOption({
  index,
  active,
  primary,
  status,
  secondary,
  detail,
  onSelect,
}: {
  index: number;
  active: boolean;
  primary: string;
  status?: string;
  secondary: string;
  detail: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      id={`dispatch-lookup-result-${index}`}
      role="option"
      aria-selected={active}
      tabIndex={-1}
      className={cn(
        "cursor-pointer rounded-md border px-2.5 py-2 text-left outline-none",
        active
          ? "border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200"
          : "border-zinc-200 hover:bg-zinc-50"
      )}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-xs font-semibold text-zinc-900">
          {primary}
        </span>
        {status && (
          <StatusBadge status={status} className="h-5 px-1.5 text-[10px]" />
        )}
      </div>
      <div className="mt-0.5 truncate text-xs text-zinc-700">{secondary}</div>
      <div className="truncate text-[11px] text-zinc-500">{detail}</div>
    </button>
  );
}
